import { useCallback, useEffect, useRef, useState } from "react";

// #2190 — announcer-fe no longer caches anything locally (no IndexedDB, no
// stale-while-revalidate layer over a local store): "the announcer does not
// need offline durability and the caching costs more than it is worth"
// (card description). This hook replaces every announcer-fe use of
// common-fe's useCachedResource (which is still used by scribe-fe/
// scoreadmin-fe, out of this card's scope, and stays untouched).
//
// The contract is deliberately narrower than useCachedResource's:
//   fetchRemote() -> fresh data. No readLocal, no cache write, nothing
//   persisted anywhere.
//
// A connectivity loss must ONLY stop refresh (card title): whatever is
// already on screen stays on screen, no blanking, no error state.
//   - `loading` is true only until the FIRST fetch this mount has ever
//     settled (success or failure) — a later poll/dep-bump never flips it
//     back to true, so an already-populated screen never re-shows a loading
//     skeleton just because a revalidate is in flight.
//   - On success, `data` is replaced with the fresh result.
//   - On failure: if something has already loaded once this session, `data`
//     and `error` are left exactly as they were — a network blip degrades to
//     "the next poll didn't land", nothing else. Only a failure with NOTHING
//     ever loaded (a cold start with no connectivity) settles to
//     `{ data: null, error }`, matching the old "no cache to fall back to"
//     branch's behaviour minus the cache.
//
// #2217 — `resourceKey` (optional third arg) names WHICH resource this hook
// instance is fetching, e.g. `[showId, ring]`. This is the piece #2190's
// "never re-blank" contract above was silent on: it protects a REVALIDATE of
// the same resource (a poll, `refetch()`, a `nonce` bump), but a screen that
// is not remounted on a ring switch (OrderOfGo has no `key`, see #2217's
// card) makes a brand-new resource's first fetch look, to this hook,
// identical to a revalidate of the old one — same call site, same shape,
// `deps` just happens to contain a different ring. Without `resourceKey`
// that dep bump left the OLD ring's `data`/`hasLoadedRef` in place through
// the whole in-flight window, so the screen kept rendering the ring the
// announcer just left. When `resourceKey`'s serialized value differs from
// the previous run's, this is treated as a fresh mount would be: `data`
// resets to `null`, `loading` back to `true`, `hasLoadedRef` back to
// `false` — so the SAME first-load skeleton this hook already gives a
// mounting component now also covers a ring switch, with no new render
// branch for callers to get wrong. A caller that omits `resourceKey` keeps
// today's exact behaviour (`undefined !== undefined` is never true), which
// is why `useRoster`/`useClassesForShow` (no ring-scoped identity to lose)
// are untouched by this card.
//
// #2227: `initialValue` (optional 4th arg, default `null`) is the "nothing
// answered yet" value: it seeds the very first render AND is what a
// `resourceKey` reset restores AND is what a cold-start failure (nothing
// loaded yet this key) settles `data` to. #2217 hard-coded all three to
// `null`, which is safe for a caller where `null` only ever means "empty":
// but useRingStateForRing (#2197) gives `null` a second, stronger meaning:
// "server confirmed no live class" (see resolveRingTruth, rule 8), and
// reserves `undefined` for "not answered yet." Hard-coding `null` meant a
// resourceKey reset whose first fetch on the NEW key failed produced the
// exact bit pattern `resolveRingTruth` reads as a real, confirmed answer:
// OrderOfGo's `ringStateKnown` flipped true off a fetch that never landed,
// rendering `oog-no-class-in-progress` as if the server had spoken (#2227's
// card, chosen as this card's design: Option A, a caller-supplied
// "nothing yet" value, over Option B (re-deriving honesty per consumer in
// OrderOfGo, the same per-consumer trade #2217 already argued against for
// ring-tracking), and over Option C alone, which stops re-arming
// `hasLoadedRef` but still leaves "what does a failed new-key fetch show"
// unanswered; parameterizing the settle value answers both at once with no
// new render branch). `useRingStateForRing` now passes `undefined` here, so
// a reset and a cold-start failure both settle to the SAME sentinel its
// mount-time "ring not resolved yet" case already uses: `ringStateKnown`
// stays false and OrderOfGo's honest Skeleton renders instead of the false
// banner. Every other caller keeps the default `null`, unchanged.
//
// Deliberately a rest param, not `initialValue = null`: a plain default
// parameter substitutes `null` for ANY `undefined` argument, including one
// passed on purpose. `useRemoteResource(fn, deps, key, undefined)` would
// silently collapse right back to `null` and defeat the whole point of
// useRingStateForRing passing `undefined` explicitly. `initialValueArgs`
// distinguishes "the caller passed a 4th argument" (`.length === 1`, use
// `initialValueArgs[0]` as-is, `undefined` included) from "the caller
// omitted it" (`.length === 0`, default to `null`), which `= null` cannot.
export function useRemoteResource(fetchRemote, deps = [], resourceKey = undefined, ...initialValueArgs) {
  const initialValue = initialValueArgs.length > 0 ? initialValueArgs[0] : null;
  const [state, setState] = useState({ data: initialValue, loading: true, error: null });
  const [refreshing, setRefreshing] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const hasLoadedRef = useRef(false);
  const resourceKeyRef = useRef(undefined);

  useEffect(() => {
    let cancelled = false;
    if (resourceKey !== undefined) {
      const serializedKey = JSON.stringify(resourceKey);
      const isKeyChange =
        resourceKeyRef.current !== undefined && resourceKeyRef.current !== serializedKey;
      resourceKeyRef.current = serializedKey;
      if (isKeyChange) {
        hasLoadedRef.current = false;
        setState({ data: initialValue, loading: true, error: null });
      }
    }
    (async () => {
      try {
        const fresh = await fetchRemote();
        if (cancelled) return;
        hasLoadedRef.current = true;
        setState({ data: fresh, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        if (!hasLoadedRef.current) {
          // Never loaded anything yet this key: settle to the caller's
          // "nothing yet" value, not a permanent skeleton, same as a
          // genuinely empty result would (see #2227 doc above).
          setState({ data: initialValue, loading: false, error: err });
        }
        // else: already showing real data — leave it, and `error`, exactly
        // as they were. This IS "a connectivity loss only stops refresh".
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadNonce]);

  // Forces an immediate re-fetch (#908 manual refresh button), same
  // refetch()/refreshing contract useCachedResource's callers already rely
  // on.
  const refetch = useCallback(() => {
    setRefreshing(true);
    setReloadNonce((n) => n + 1);
  }, []);

  return { ...state, refetch, refreshing };
}

export default useRemoteResource;
